"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { getPanchang, type PanchangResponse } from "@/lib/api";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import {
  PanchangResultSkeleton,
  TithiNakshatraCard,
  QuickInfoGrid,
  YogaKaranaRow,
  InauspiciousPeriodsCard,
  PlanetPositionsDisclosure,
  WhatIsPanchangDisclosure,
} from "./Panchang.sections";

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function toDateString(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const y = values.year;
  const m = values.month;
  const d = values.day;
  return `${y}-${m}-${d}`;
}

export interface PanchangProps {
  latitude?: number;
  longitude?: number;
  language?: "en" | "hi";
  initialDate?: string;
  initialDisplayDate?: string;
  initialResult?: PanchangResponse | null;
}

export function Panchang({
  latitude = 28.6139,
  longitude = 77.209,
  language = "en",
  initialDate,
  initialDisplayDate,
  initialResult = null,
}: PanchangProps) {
  const isHindi = language === "hi";
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PanchangResponse | null>(initialResult);
  const trackedInitialResult = useRef(false);
  const today = initialDate ? new Date(`${initialDate}T00:00:00+05:30`) : new Date();
  const todayFormatted = initialDisplayDate ?? formatDate(today);
  const apiDate = initialDate ?? toDateString(today);

  useEffect(() => {
    if (initialResult?.date === apiDate && !trackedInitialResult.current) {
      trackedInitialResult.current = true;
      trackCalculatorUsed({ calculator: "panchang" });
      return;
    }

    async function fetchPanchang() {
      setLoading(true);
      setError(null);
      try {
        const response = await getPanchang({
          date: apiDate,
          latitude,
          longitude,
          tz_offset: 5.5,
        });
        setResult(response);
        trackCalculatorUsed({ calculator: "panchang" });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : isHindi
              ? "पंचांग लोड नहीं हो सका"
              : "Failed to load panchang"
        );
      } finally {
        setLoading(false);
      }
    }
    fetchPanchang();
  }, [apiDate, initialResult, isHindi, latitude, longitude]);

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <PanchangHeader isHindi={isHindi} todayFormatted={todayFormatted} />

        <div className="min-h-[1120px]">
          {loading && <PanchangResultSkeleton />}

          {error && !loading && (
            <div className="card-sacred p-6 text-center border-error-tint bg-error/5">
              <p className="text-[14px] text-error">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-[13px] font-semibold text-ish-accent"
              >
                Try Again
              </button>
            </div>
          )}

          {result && !loading && (
            <div className="space-y-6">
              <TithiNakshatraCard result={result} />
              <QuickInfoGrid result={result} />
              <YogaKaranaRow result={result} />
              <InauspiciousPeriodsCard result={result} />
              <PlanetPositionsDisclosure result={result} />
              <div className="text-center">
                <Link
                  href="/free-kundali"
                  className="btn-sacred inline-block font-semibold py-3 px-6 rounded-lg text-[14px]"
                >
                  Get Your Personal Kundali &rarr;
                </Link>
              </div>
            </div>
          )}
        </div>

        <WhatIsPanchangDisclosure />
        <CalculatorCrossLinks currentPage="panchang" />
      </div>
    </div>
  );
}

function PanchangHeader({ isHindi, todayFormatted }: { isHindi: boolean; todayFormatted: string }) {
  return (
    <div className="text-center mb-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
        {isHindi ? "आज का पंचांग" : "Aaj Ka Panchang"}
      </p>
      <h1 className="text-[36px] sm:text-[40px] font-bold leading-tight text-ish-dark font-serif">
        {isHindi ? "आज का पंचांग" : "Today's Panchang"}
      </h1>
      <p className="mt-2 text-[14px] text-ish-muted">{todayFormatted}</p>
      <p className="mt-1 text-[12px] text-ish-faint">
        {isHindi
          ? "तिथि, नक्षत्र, योग, करण और राहु काल — नई दिल्ली के लिए"
          : "Tithi, Nakshatra, Yoga, Karana & Rahu Kaal for New Delhi"}
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium text-ish-faint">
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          {isHindi ? "100% सटीक" : "100% Accurate"}
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          {isHindi ? "प्रतिदिन अद्यतन" : "Updated Daily"}
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {isHindi ? "100% निःशुल्क" : "100% Free"}
        </span>
      </div>
    </div>
  );
}
